<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          View Raise Complaint Details
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="6">
              <v-text-field
                :value="form.complaint_type ? form.complaint_type.description : ''"
                outlined
                label="Complaint Type"
                readonly
                prepend-inner-icon="mdi-format-list-bulleted"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                :value="form.store ? form.store.business_name : 'No Store'"
                outlined
                label="Store"
                readonly
                prepend-inner-icon="mdi-store"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="12">
              <v-textarea
                v-model="form.description"
                outlined
                label="Description"
                readonly
                prepend-inner-icon="mdi-text"
                rows="4"
              ></v-textarea>
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="6">
              <v-text-field
                :value="getStatusText(form.status)"
                outlined
                label="Status"
                readonly
                prepend-inner-icon="mdi-information"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                :value="getUserFullName()"
                outlined
                label="Created By"
                readonly
                prepend-inner-icon="mdi-account"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row v-if="form.remarks">
            <v-col sm="12">
              <v-textarea
                v-model="form.remarks"
                outlined
                label="Remarks"
                readonly
                prepend-inner-icon="mdi-comment-text"
                rows="3"
              ></v-textarea>
            </v-col>
          </v-row>
          
          <!-- Documents Section -->
          <v-row v-if="form.documents && form.documents.length > 0">
            <v-col sm="12">
              <v-card outlined>
                <v-card-title class="py-3">
                  <v-icon class="mr-2">mdi-file-multiple</v-icon>
                  Attached Documents ({{ form.documents.length }})
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col
                      v-for="(document, index) in form.documents"
                      :key="index"
                      sm="6"
                      md="4"
                    >
                      <v-card outlined hover>
                        <v-card-text class="text-center pa-3">
                          <v-icon size="48" color="primary" class="mb-2">
                            {{ getDocumentIcon(document.doc_path) }}
                          </v-icon>
                          <div class="text-subtitle-2 font-weight-medium">
                            {{ getDocumentName(document, index) }}
                          </div>
                          <div class="mt-2">
                            <v-btn
                              small
                              color="primary"
                              :href="mediaUrl + document.doc_path"
                              target="_blank"
                            >
                              <v-icon left small>mdi-eye</v-icon>
                              View
                            </v-btn>
                            <v-btn
                              small
                              color="red"
                              text
                              class="ml-2"
                              @click="confirmDeleteDocument(document.id)"
                              v-if="canManageDocuments"
                            >
                              <v-icon left small>mdi-delete</v-icon>
                              Delete
                            </v-btn>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          
          <!-- Add Documents Section (if user can manage) -->
          <v-row v-if="canManageDocuments">
            <v-col sm="12">
              <v-card outlined>
                <v-card-title class="py-3">
                  <v-icon class="mr-2">mdi-plus</v-icon>
                  Add More Documents
                </v-card-title>
                <v-card-text>
                  <v-file-input
                    v-model="newDocuments"
                    outlined
                    multiple
                    label="Upload Additional Documents"
                    accept="image/*,application/pdf,.doc,.docx"
                    prepend-inner-icon="mdi-file-multiple"
                    show-size
                  ></v-file-input>
                  <v-btn
                    v-if="newDocuments && newDocuments.length > 0"
                    color="primary"
                    :loading="isUploadingDocuments"
                    @click="uploadDocuments"
                    class="mt-2"
                  >
                    Upload {{ newDocuments.length }} Document(s)
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      
      <!-- Delete Document Confirmation Dialog -->
      <v-dialog v-model="deleteDocumentDialog" max-width="500px">
        <v-card>
          <v-card-title class="headline">
            <v-icon color="red" class="mr-2">mdi-delete</v-icon>
            Delete Document
          </v-card-title>
          <v-card-text>
            Are you sure you want to delete this document? This action cannot be undone.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="deleteDocumentDialog = false">Cancel</v-btn>
            <v-btn color="red" text @click="deleteDocument" :loading="isDeletingDocument">
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>
<script>
import axios from "axios";

export default {
  name: "RaiseComplaintDetailPage",
  data() {
    return {
      form: {},
      isLoading: false,
      newDocuments: [],
      isUploadingDocuments: false,
      deleteDocumentDialog: false,
      isDeletingDocument: false,
      documentToDelete: null,
    };
  },
  computed: {
    canManageDocuments() {
      // Allow document management for admin users or complaint owner
      return this.user && (
        (this.user.position && this.user.position.name === 'ADMIN') || 
        this.user.id === this.form.user_id
      );
    }
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      let encryptedId = this.$route.params.raiseComplaintId;
      try {
        const response = await axios.get(`/raise_complaints/${encryptedId}`);
        this.form = response.data.data;
      } catch (error) {
        console.error("Error fetching data:", error);
        this.$toast.error("Error loading complaint details");
      }
      this.isLoading = false;
    },
    
    getStatusText(status) {
      switch(status) {
        case 0: return 'Pending';
        case 1: return 'Approved';
        case 2: return 'Rejected';
        default: return 'Unknown';
      }
    },
    
    getUserFullName() {
      if (!this.form.user) return '';
      const firstName = this.form.user.first_name || '';
      const lastName = this.form.user.last_name || '';
      return `${firstName} ${lastName}`.trim();
    },
    
    getDocumentIcon(filePath) {
      const extension = filePath.split('.').pop().toLowerCase();
      
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) {
        return 'mdi-file-image';
      } else if (extension === 'pdf') {
        return 'mdi-file-pdf-box';
      } else if (['doc', 'docx'].includes(extension)) {
        return 'mdi-file-word';
      }
      return 'mdi-file-document';
    },
    
    getDocumentName(document, index) {
      return document.original_filename || `Document ${index + 1}`;
    },
    
    async uploadDocuments() {
      try {
        this.isUploadingDocuments = true;
        
        const formData = new FormData();
        formData.append('status', this.form.status); // Required field
        
        // Add new documents
        this.newDocuments.forEach((file) => {
          formData.append('doc_path[]', file);
        });
        
        await axios.patch(
          `/raise_complaints/${this.$route.params.raiseComplaintId}`, 
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        this.$toast.success("Documents uploaded successfully!");
        this.newDocuments = [];
        await this.getData(); // Refresh data
        
      } catch (error) {
        console.error("Error uploading documents:", error);
        this.$toast.error("Error uploading documents");
      }
      this.isUploadingDocuments = false;
    },
    
    confirmDeleteDocument(documentId) {
      this.documentToDelete = documentId;
      this.deleteDocumentDialog = true;
    },
    
    async deleteDocument() {
      try {
        this.isDeletingDocument = true;
        
        await axios.post('/raise_complaints/delete_document', {
          document_id: this.documentToDelete
        });
        
        this.$toast.success("Document deleted successfully!");
        this.deleteDocumentDialog = false;
        this.documentToDelete = null;
        await this.getData(); // Refresh data
        
      } catch (error) {
        console.error("Error deleting document:", error);
        this.$toast.error("Error deleting document");
      }
      this.isDeletingDocument = false;
    },
  },
};
</script>
